const { debounce } = window.BAO.Utils

const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    carousel.on('created', carousel =>
      carousel.container.setAttribute(`data-initialised`, true),
    )
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} arrows
 */
const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details
      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', (maxIdx === 0).toString())
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', (maxIdx === 0).toString())
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
    window.addEventListener('resize', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    const updateDotStates = () => {
      const { progress } = carousel.track.details
      const activeIndex = Number.isFinite(progress)
        ? Math.round(progress * (dots.length - 1))
        : 0

      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === activeIndex))
      })
    }

    const dotClickHandlers = dots.map((dot, index) => () =>
      carousel.moveToIdx(index),
    )

    dots.forEach((dot, index) => {
      dot.addEventListener('click', dotClickHandlers[index])
    })

    carousel.on('created', updateDotStates)
    carousel.on('detailsChanged', updateDotStates)

    carousel.on('destroyed', () => {
      dots.forEach((dot, index) => {
        dot.removeEventListener('click', dotClickHandlers[index])
      })
    })
  }
}

/** @param {number} idx */
const useResetIdxOnResize = (idx = 0) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    window.addEventListener(
      'resize',
      debounce(() => carousel.moveToIdx(idx), 200),
    )
  }
}

const addWheelControls = direction => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    let touchTimeout = null
    let wheelActive = false
    let position = {}

    if (carousel.options.disabled) return

    carousel?.container?.style?.setProperty(
      direction === 'horizontal'
        ? 'overscroll-behavior-x'
        : 'overscroll-behavior-y',
      'contain',
    )

    const dispatch = (event, name) => {
      position.x -= event.deltaX
      position.y -= event.deltaY
      carousel.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheelStart = event => {
      position = {
        x: event.pageX,
        y: event.pageY,
      }
      dispatch(event, 'ksDragStart')
    }

    const wheel = event => {
      dispatch(event, 'ksDrag')
    }

    const wheelEnd = event => {
      dispatch(event, 'ksDragEnd')
    }

    const eventWheel = event => {
      let supportWheel = true
      let earlyReturn = false

      /* Ensure we don't block vertical scrolling for horizontal carousels */
      if (
        direction === 'horizontal' &&
        (event?.deltaY > 1 || event?.deltaY < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Ensure we don't block horizontal scrolling for vertical carousels */
      if (
        !earlyReturn &&
        direction === 'vertical' &&
        (event?.deltaX > 1 || event?.deltaX < -1)
      ) {
        supportWheel = false
        earlyReturn = true
      }

      /* Prevent overscrolling for none looping carousels */
      if (!earlyReturn && carousel.options.loop === false) {
        const { details } = carousel?.track
        const scrollIndex =
          direction === 'horizontal' ? event?.deltaX : event?.deltaY
        if (
          (details.progress <= 0 && scrollIndex < 0) ||
          (details.position >= details.max && scrollIndex > 0)
        ) {
          earlyReturn = true
        }
      }

      // If we are on the last slide, allow user to scroll the page down.
      if (direction === 'vertical') {
        const isLastSlide =
          carousel?.track?.details?.abs === carousel?.track?.details?.maxIdx

        if (isLastSlide && event?.deltaY > 0) {
          supportWheel = false
          earlyReturn = true
        }
      }

      /* Prevent default scroll functionality for carousels supporting wheel and scrolling on the correct plane */
      if (supportWheel) {
        event.preventDefault()
      }

      if (earlyReturn) {
        if (touchTimeout && wheelActive) {
          clearTimeout(touchTimeout)
          wheelActive = false
          wheelEnd(event)
        }
        return
      }

      if (!wheelActive) {
        wheelStart(event)
        wheelActive = true
      }
      wheel(event)
      clearTimeout(touchTimeout)
      touchTimeout = setTimeout(() => {
        wheelActive = false
        wheelEnd(event)
      }, 250)
    }

    carousel.on('created', () => {
      carousel.container.addEventListener('wheel', eventWheel, {
        passive: false,
      })
    })

    carousel.on('destroyed', () => {
      carousel.container.removeEventListener('wheel', eventWheel, {
        passive: false,
      })
      clearTimeout(touchTimeout)
    })
  }
}

/** @param {HTMLButtonElement[]} dots */
const useReveal = (slides = []) => {
  /** @param {import('keen-slider').KeenSliderInstance} carousel */
  return carousel => {
    carousel.on('created', () => {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      }

      const observer = new IntersectionObserver(reveal, options)
      observer.observe(carousel.container)
    })

    function reveal (entries, observer) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          carousel.animator.start([
            { distance: 0.1, duration: 1000, easing: t => easeInOut(t) },
            { distance: -0.1, duration: 1000, easing: t => easeInOut(t) },
          ])
          observer.unobserve(entry.target)
        }
      })
    }

    function easeInOut (t) {
      return -(Math.cos(Math.PI * t) - 1) / 2
    }
  }
}

window.BAO.Carousel = window.BAO.Carousel || {}
window.BAO.Carousel.plugins = {
  setInitialised,
  useArrows,
  useAutoplay,
  useDots,
  useResetIdxOnResize,
  addWheelControls,
  useReveal,
}
